import React, { useState, FunctionComponent } from "react";
import AsyncSelect, {
  Props as ReactAsyncSelectProps,
} from "react-select/async";
import { getIn, FieldProps } from "formik";
import { emphasize } from "@material-ui/core/styles/colorManipulator";
import FormHelperText from "@material-ui/core/FormHelperText";
import { makeStyles, Theme } from "@material-ui/core/styles";

const useStyles = makeStyles(({ spacing, palette, brand }: Theme) => ({
  input: (props: { margin: string }) => ({
    display: "flex",
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: props.margin === "dense" ? "37.63px" : "53.63px",
    margin: 0,
    height: "auto",
  }),
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
    lineHeight: "0.5rem",
    padding: spacing(1, 0),
  },
  chip: {
    height: 26,
    color: brand.stormyBlue[100],
    background: "#E3E8EF",
    margin: spacing(0.5, 0.25),
  },
  chipFocused: {
    backgroundColor: emphasize(
      palette.type === "light" ? palette.grey[300] : palette.grey[700],
      0.08
    ),
  },
  chipInvalid: {
    background: "#FED6DC",
  },
  noOptionsMessage: {
    padding: spacing(1, 2),
  },
  singleValue: {},
  placeholder: {
    position: "absolute",
  },
  helperText: {
    margin: spacing(1, 0),
  },
}));

type TAsyncSelectFieldProps = ReactAsyncSelectProps<any> & {
  loadOptions: (inputValue: string) => [];
};
type TAsyncSelectFieldFormikProps = TAsyncSelectFieldProps & FieldProps;

export const AsyncSelectField: FunctionComponent<TAsyncSelectFieldFormikProps> = ({
  isMulti = false,
  disabled = false,
  placeholder = "",
  margin = "dense",
  loadOptions,
  ...props
}) => {
  const customClasses = useStyles({ margin });
  const {
    field,
    form,
    ...selectFieldProps
  } = props as TAsyncSelectFieldFormikProps;
  const isFormikConnected = form && field;
  const [inputValue, setInputValue] = useState<string>("");

  if (!isFormikConnected) {
    return null;
  }

  const handleInputChange = (inputValue = "") => setInputValue(inputValue);

  const handleValueChange = (selectValue: any) => {
    if (!selectValue || selectValue.length === 0) {
      form.setFieldValue(field.name, null);
      return;
    }

    form.setFieldValue(field.name, selectValue);
  };

  const fieldError: string | string[] = getIn(form.errors, field.name);
  const showError = getIn(form.touched, field.name) && Boolean(fieldError);
  const errorMessage = Array.isArray(fieldError)
    ? fieldError.filter((e) => Boolean(e))[0]
    : fieldError;

  return (
    <>
      <AsyncSelect
        classes={customClasses}
        isMulti={isMulti}
        value={field.value}
        placeholder={placeholder}
        onChange={handleValueChange}
        onInputChange={handleInputChange}
        isDisabled={disabled}
        showError={showError}
        margin={margin}
        inputValue={inputValue}
        cacheOptions
        // @ts-ignore
        loadOptions={loadOptions}
        {...selectFieldProps}
      />
      {showError && (
        <FormHelperText error className={customClasses.helperText}>
          {errorMessage}
        </FormHelperText>
      )}
    </>
  );
};
